import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { submitLead, formatApiError, BUSINESS_TYPE_OPTIONS, BUSINESS_SIZE_OPTIONS, INTEREST_OPTIONS, INTEREST_LABELS, serviceFromQuery } from "../lib/api";

const STEPS = ["Business", "Size", "Opportunities", "Details"];

export default function AssessmentWizard() {
  const location = useLocation();
  const preset = useMemo(() => serviceFromQuery(new URLSearchParams(location.search).get("service")), [location.search]);

  const [step, setStep] = useState(0);
  const [businessType, setBusinessType] = useState("");
  const [businessSize, setBusinessSize] = useState("");
  const [interests, setInterests] = useState(preset ? [preset] : []);
  const [form, setForm] = useState({ contact_name: "", business_name: "", email: "", phone: "", comments: "", consent_privacy: false, consent_marketing: false });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [ref, setRef] = useState(null);
  const honeypot = useRef("");
  const startedAt = useRef(Date.now());

  useEffect(() => {
    if (preset) setInterests((v) => (v.includes(preset) ? v : [preset, ...v]));
    startedAt.current = Date.now();
  }, [preset]);

  const toggleInterest = (val) =>
    setInterests((v) => (v.includes(val) ? v.filter((x) => x !== val) : [...v, val]));

  const next = () => {
    setError("");
    if (step === 0 && !businessType) return setError("Please choose what best describes your business.");
    if (step === 1 && !businessSize) return setError("Please choose your team size.");
    if (step === 2 && interests.length === 0) return setError("Select at least one area — or choose “Not sure — help me decide”.");
    setStep((s) => Math.min(s + 1, 3));
  };
  const back = () => { setError(""); setStep((s) => Math.max(s - 1, 0)); };

  const validateDetails = () => {
    if (!form.contact_name.trim()) return "Please add your name.";
    if (!form.business_name.trim()) return "Please add your business name.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Please add a valid business email.";
    if (!form.consent_privacy) return "Please accept the privacy policy to continue.";
    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const v = validateDetails();
    if (v) return setError(v);
    setBusy(true);
    try {
      const res = await submitLead({
        ...form,
        business_type: businessType,
        business_size: businessSize,
        interests,
        source_page: window.location.pathname + window.location.search,
        source_service: preset || (interests.length === 1 ? interests[0] : null),
        honeypot: honeypot.current ? honeypot.current.value : "",
        elapsed_ms: Date.now() - startedAt.current,
      });
      setRef(res.lead_id);
      window.scrollTo({ top: window.scrollY, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = (bad) =>
    `w-full rounded-xl border px-4 py-3 text-[15px] bg-white text-navy placeholder:text-slate-light focus:outline-none focus:ring-2 focus:ring-teal/60 ${bad ? "border-red-400" : "border-line"}`;

  const stepVariants = {
    enter: { x: 48, opacity: 0 },
    center: { x: 0, opacity: 1, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } },
    exit: { x: -48, opacity: 0, transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] } },
  };

  if (ref) {
    return (
      <div className="relative rounded-[28px] bg-navy-950 text-white p-8 md:p-12 overflow-hidden grain" data-testid="wizard-success">
        <div className="absolute inset-0 bg-arch-dark" aria-hidden="true" />
        <div className="relative text-center max-w-2xl mx-auto py-6">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 220, damping: 14 }} className="mx-auto inline-flex items-center justify-center w-16 h-16 rounded-full bg-teal">
            <CheckCircle2 className="w-8 h-8 text-navy" />
          </motion.div>
          <h3 className="mt-6 font-display font-extrabold tracking-tight text-2xl md:text-3xl leading-snug">
            Your Business Opportunity Review Is Ready for the Next Step.
          </h3>
          <p className="mt-4 text-[15px] leading-relaxed text-ice/75">
            Thank you for sharing a little about your business. We'll review your interests and help you identify
            which specialist resources may be worth exploring.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2" data-testid="wizard-summary-chips">
            {interests.map((i) => (
              <span key={i} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-3.5 py-1.5 text-xs font-bold text-teal-bright">
                <Check className="w-3 h-3" /> {INTEREST_LABELS[i]}
              </span>
            ))}
          </div>
          <p className="mt-6 text-[11px] text-ice/45">Reference: {ref.slice(0, 8).toUpperCase()} · No savings estimate is implied — a specialist review is the next step, with no obligation.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative rounded-[28px] bg-white border border-line shadow-lift overflow-hidden" data-testid="assessment-wizard">
      {/* progress */}
      <div className="px-6 md:px-10 pt-8">
        <div className="flex items-center justify-between gap-2" data-testid="wizard-progress">
          {STEPS.map((label, i) => (
            <div key={label} className="flex-1">
              <div className="h-1.5 rounded-full bg-ice overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-royal to-teal"
                  initial={false}
                  animate={{ width: step >= i ? "100%" : "0%" }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                />
              </div>
              <p className={`mt-2 text-[10px] md:text-[11px] font-bold uppercase tracking-wider2 ${step >= i ? "text-royal" : "text-slate-light"}`}>
                {i + 1}. {label}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="px-6 md:px-10 pb-8 md:pb-10 pt-8">
        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div key="s0" variants={stepVariants} initial="enter" animate="center" exit="exit" data-testid="wizard-step-1">
              <h3 className="font-display font-extrabold tracking-tight text-navy text-xl md:text-2xl">What best describes your business?</h3>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {BUSINESS_TYPE_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    data-testid={`wizard-option-type-${o.value}`}
                    onClick={() => setBusinessType(o.value)}
                    className={`flex items-center justify-between rounded-xl border px-5 py-4 text-left text-[15px] font-semibold transition-all duration-200 ${businessType === o.value ? "border-teal bg-teal-soft text-navy shadow-card" : "border-line text-slate hover:border-royal hover:text-navy"}`}
                  >
                    {o.label}
                    {businessType === o.value && <Check className="w-4 h-4 text-teal-dark" />}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div key="s1" variants={stepVariants} initial="enter" animate="center" exit="exit" data-testid="wizard-step-2">
              <h3 className="font-display font-extrabold tracking-tight text-navy text-xl md:text-2xl">How many people work for your business?</h3>
              <div className="mt-6 flex flex-wrap gap-3">
                {BUSINESS_SIZE_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    data-testid={`wizard-option-size-${o.value}`}
                    onClick={() => setBusinessSize(o.value)}
                    className={`rounded-xl border px-6 py-3.5 text-[15px] font-semibold transition-all duration-200 ${businessSize === o.value ? "border-teal bg-teal-soft text-navy shadow-card" : "border-line text-slate hover:border-royal hover:text-navy"}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="s2" variants={stepVariants} initial="enter" animate="center" exit="exit" data-testid="wizard-step-3">
              <h3 className="font-display font-extrabold tracking-tight text-navy text-xl md:text-2xl">Which areas would you like to explore?</h3>
              <p className="mt-2 text-sm text-slate">Select all that apply.</p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {INTEREST_OPTIONS.map((o) => {
                  const on = interests.includes(o.value);
                  return (
                    <button
                      key={o.value}
                      type="button"
                      data-testid={`wizard-option-interest-${o.value}`}
                      aria-pressed={on}
                      onClick={() => toggleInterest(o.value)}
                      className={`flex items-center justify-between rounded-xl border px-5 py-4 text-left text-[15px] font-semibold transition-all duration-200 ${on ? "border-teal bg-teal-soft text-navy shadow-card" : "border-line text-slate hover:border-royal hover:text-navy"}`}
                    >
                      <span className="flex items-center gap-3">
                        <span className={`inline-flex items-center justify-center w-5 h-5 rounded-md border ${on ? "bg-teal border-teal" : "border-slate-light"}`}>
                          {on && <Check className="w-3.5 h-3.5 text-navy" />}
                        </span>
                        {o.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.form key="s3" variants={stepVariants} initial="enter" animate="center" exit="exit" noValidate onSubmit={onSubmit} data-testid="wizard-step-4">
              <h3 className="font-display font-extrabold tracking-tight text-navy text-xl md:text-2xl">Where can we send your review?</h3>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="wz-name" className="block text-xs font-bold uppercase tracking-wider2 text-royal mb-1.5">Contact name *</label>
                  <input id="wz-name" data-testid="wizard-input-contact-name" className={inputCls(false)} value={form.contact_name} onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))} placeholder="Jane Smith" autoComplete="name" />
                </div>
                <div>
                  <label htmlFor="wz-business" className="block text-xs font-bold uppercase tracking-wider2 text-royal mb-1.5">Business name *</label>
                  <input id="wz-business" data-testid="wizard-input-business-name" className={inputCls(false)} value={form.business_name} onChange={(e) => setForm((f) => ({ ...f, business_name: e.target.value }))} placeholder="Smith & Co." autoComplete="organization" />
                </div>
                <div>
                  <label htmlFor="wz-email" className="block text-xs font-bold uppercase tracking-wider2 text-royal mb-1.5">Business email *</label>
                  <input id="wz-email" type="email" data-testid="wizard-input-email" className={inputCls(false)} value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="jane@yourbusiness.com" autoComplete="email" />
                </div>
                <div>
                  <label htmlFor="wz-phone" className="block text-xs font-bold uppercase tracking-wider2 text-royal mb-1.5">Phone (optional)</label>
                  <input id="wz-phone" type="tel" data-testid="wizard-input-phone" className={inputCls(false)} value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="(555) 000-0000" autoComplete="tel" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="wz-comments" className="block text-xs font-bold uppercase tracking-wider2 text-royal mb-1.5">Additional comments (optional)</label>
                  <textarea id="wz-comments" rows="3" data-testid="wizard-input-comments" className={inputCls(false)} value={form.comments} onChange={(e) => setForm((f) => ({ ...f, comments: e.target.value }))} placeholder="Anything specific you'd like us to know?" />
                </div>
              </div>

              <div className="absolute -left-[9999px]" aria-hidden="true">
                <label htmlFor="wz-website">Website</label>
                <input id="wz-website" name="website_url" tabIndex={-1} autoComplete="off" ref={honeypot} />
              </div>

              <div className="mt-5 space-y-3">
                <label className="flex items-start gap-3 text-[13px] leading-relaxed text-slate">
                  <input type="checkbox" data-testid="wizard-consent-privacy" className="mt-0.5 w-4 h-4 accent-[#15B8A6]" checked={form.consent_privacy} onChange={(e) => setForm((f) => ({ ...f, consent_privacy: e.target.checked }))} />
                  <span>I agree to be contacted about my inquiry and accept the privacy policy and terms of use. *</span>
                </label>
                <label className="flex items-start gap-3 text-[13px] leading-relaxed text-slate">
                  <input type="checkbox" data-testid="wizard-consent-marketing" className="mt-0.5 w-4 h-4 accent-[#15B8A6]" checked={form.consent_marketing} onChange={(e) => setForm((f) => ({ ...f, consent_marketing: e.target.checked }))} />
                  <span>Occasionally send me new savings opportunities worth knowing about. (optional)</span>
                </label>
              </div>
            </motion.form>
          )}
        </AnimatePresence>

        {error && (
          <p className="mt-4 text-sm font-semibold text-red-500" data-testid="wizard-error" role="alert">{error}</p>
        )}

        <div className="mt-8 flex items-center justify-between gap-3">
          <button
            type="button"
            data-testid="wizard-back-button"
            onClick={back}
            disabled={step === 0}
            className="inline-flex items-center gap-2 text-sm font-bold text-slate hover:text-navy transition-colors disabled:opacity-0"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {step < 3 ? (
            <button
              type="button"
              data-testid={`wizard-step-${step + 1}-next-button`}
              onClick={next}
              className="group inline-flex items-center gap-2 bg-navy text-white font-bold text-[15px] px-7 py-3.5 rounded-full transition-all duration-300 hover:bg-teal hover:text-navy"
            >
              Continue
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          ) : (
            <button
              type="submit"
              data-testid="wizard-submit-assessment-button"
              onClick={onSubmit}
              disabled={busy}
              className="group inline-flex items-center gap-2 bg-teal text-navy font-bold text-[15px] px-7 py-3.5 rounded-full transition-all duration-300 hover:bg-teal-bright disabled:opacity-60"
            >
              <Sparkles className="w-4 h-4" />
              {busy ? "Submitting…" : "Get My Review Started"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
